import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Paper,
  Stack,
  Avatar,
  Menu,
  MenuItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Person as PersonIcon,
  SmartToy as BotIcon,
  Build as BuildIcon,
  Chat as ChatIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { sendChatMessageV2, useGetChatHistory, useGetChatList, useGetPunchListItems, updatePunchListItem } from '../../api/chat';
import { useChatSubscription } from '../../hooks/useChatSubscription.js'
import { useChannelSubscription } from '../../hooks/useAppSyncSubscription.js'
import UserProfileContext from '../../context/userprofilecontext.js';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { formatToolName } from '../../utils/toolNames';
import { normalizeChatText } from '../../utils/chatText';

// Generate UUID4 for new chats
const generatesessionID = () => {
  return crypto.randomUUID();
};

// Message types
const MessageType = {
  USER: 'user',
  ASSISTANT: 'assistant',
  ASSISTANT_TOOL_USE: 'assistant_tool_use',
  TOOL_RESULT: 'tool_result',
  SYSTEM: 'system',
};

// Chat states
const ChatState = {
  WAITING_FOR_USER: 'waiting_for_user',
  AI_RESPONDING: 'ai_responding',
  AI_PROCESSING: 'ai_processing',
  AI_TOOL_USE: 'ai_tool_use',
  ERROR: 'error',
};

// Status chip component
function StatusChip({ state }) {
  if (!state) return null;

  const statusConfig = {
    [ChatState.WAITING_FOR_USER]: {
      label: 'Ready',
      color: 'success',
    },
    [ChatState.AI_PROCESSING]: {
      label: 'AI Thinking...',
      color: 'primary',
    },
    [ChatState.AI_TOOL_USE]: {
      label: 'Using Tools...',
      color: 'secondary',
    },
    [ChatState.AI_RESPONDING]: {
      label: 'AI Responding...',
      color: 'info',
    },
    [ChatState.ERROR]: {
      label: 'Error',
      color: 'error',
    },
  };

  const config = statusConfig[state.state] || statusConfig[ChatState.WAITING_FOR_USER];

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      sx={{ fontWeight: 500 }}
    />
  );
}

// Message component
function ChatMessage({ message }) {
  const { message_type, content, user_name, timestamp, tool_uses } = message;
  const time = new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  // User message
  if (message_type === MessageType.USER) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Card sx={{ maxWidth: '70%', bgcolor: 'primary.main', color: 'primary.contrastText' }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.dark' }}>
                <PersonIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {user_name || 'User'} • {time}
              </Typography>
            </Stack>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {content}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Assistant message — rendered as markdown
  if (message_type === MessageType.ASSISTANT) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
        <Card sx={{ maxWidth: '70%', bgcolor: 'grey.100' }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main' }}>
                <BotIcon sx={{ fontSize: 16 }} />
              </Avatar>
              <Typography variant="caption" color="text.secondary">
                LIVI • {time}
              </Typography>
            </Stack>
            <Box sx={{
              '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
              '& ul, & ol': { mt: 0, mb: 1, pl: 2.5 },
              '& li': { mb: 0.5 },
              '& code': {
                fontFamily: '"JetBrains Mono", monospace',
                fontSize: '0.85em',
                bgcolor: 'grey.200',
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
              },
              '& pre': {
                bgcolor: 'grey.900',
                color: 'grey.100',
                p: 1.5,
                borderRadius: 1,
                overflow: 'auto',
                '& code': { bgcolor: 'transparent', color: 'inherit', p: 0 },
              },
              '& table': {
                borderCollapse: 'collapse',
                width: '100%',
                mb: 1,
                fontSize: '0.875rem',
              },
              '& th, & td': {
                border: '1px solid',
                borderColor: 'grey.300',
                px: 1,
                py: 0.5,
                textAlign: 'left',
              },
              '& th': { bgcolor: 'grey.200', fontWeight: 600 },
              '& h1, & h2, & h3, & h4': { mt: 1, mb: 0.5, fontWeight: 600 },
              '& h1': { fontSize: '1.25rem' },
              '& h2': { fontSize: '1.1rem' },
              '& h3': { fontSize: '1rem' },
              '& blockquote': {
                borderLeft: 3,
                borderColor: 'grey.400',
                pl: 1.5,
                ml: 0,
                color: 'text.secondary',
              },
              '& strong': { fontWeight: 600 },
              '& a': { color: 'primary.main' },
            }}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {normalizeChatText(content) || ''}
              </ReactMarkdown>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Tool use message
  if (message_type === MessageType.ASSISTANT_TOOL_USE) {
    const commentary = normalizeChatText(content);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
        <Card sx={{ maxWidth: '70%', bgcolor: 'secondary.50', border: 1, borderColor: 'secondary.200' }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
              <BuildIcon color="secondary" sx={{ fontSize: 20 }} />
              <Typography variant="body2" fontWeight={600} color="secondary.dark">
                Using Tools
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              {tool_uses && tool_uses.map((tool, idx) => (
                <Chip
                  key={idx}
                  label={formatToolName(tool.name)}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Stack>
            {commentary && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                {commentary}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>
    );
  }

  // System message
  if (message_type === MessageType.SYSTEM) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Chip label={content} size="small" sx={{ maxWidth: '80%' }} />
      </Box>
    );
  }

  return null;
}

// Main chat component
export default function ChatInterface() {
  const [sessionID, setsessionID] = useState(null);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const messagesEndRef = useRef(null);
  const [initialized, setInitialized] = useState(false);

  const userProfileContext = useContext(UserProfileContext);
  const userProfile = userProfileContext.userProfile;



  // Fetch chat list
  const { data: chatListData } = useGetChatList(true);
  const chatList = chatListData?.chats || [];

  // Initialize with most recent chat or new chat
  useEffect(() => {
    if (!initialized && chatListData) {
      if (chatList.length > 0) {
        // Load most recent chat
        const mostRecent = chatList[0];
        console.log('Loading most recent chat:', mostRecent.sessionID);
        setsessionID(mostRecent.sessionID);
      } else {
        // No chats exist - start with a new one
        const newsessionID = generatesessionID();
        console.log('No existing chats, generated new sessionID:', newsessionID);
        setsessionID(newsessionID);
      }
      setInitialized(true);
    }
  }, [chatListData, chatList, initialized]);

  // Punch list state
  const [punchListParent, setPunchListParent] = useState(null);

  // Build parameters for chat history
  const parameters = useMemo(
    () => sessionID ? { sessionID: sessionID, include_state: 'true' } : {},
    [sessionID]
  );

  // Fetch data with useGetChatHistory (no polling)
  const { data, error, mutate } = useGetChatHistory(parameters, !!sessionID);

  // Fetch punch list items for current session
  const punchListParams = useMemo(
    () => sessionID ? {
      scope: 'SESSION',
      sessionID: sessionID,
      ...(punchListParent ? { parentItemId: punchListParent } : {}),
    } : {},
    [sessionID, punchListParent]
  );
  const { data: punchListData, mutate: mutatePunchList } = useGetPunchListItems(punchListParams, !!sessionID);


  // subscribe to real time events
  useChatSubscription(sessionID, mutate, { enabled: !!sessionID });

  // Subscribe to punch list events — revalidate on any change
  const handlePunchListEvent = useCallback(() => {
    mutatePunchList();
  }, [mutatePunchList]);
  useChannelSubscription(sessionID, 'punchList', handlePunchListEvent);

  // Punch list toggle handler — toggle between OPEN and RESOLVED
  const handlePunchListToggle = useCallback(async (itemId) => {
    const items = punchListData?.items || [];
    const item = items.find(i => i.item_id === itemId);
    if (!item) return;
    const newStatus = (item.status === 'RESOLVED' || item.status === 'DISMISSED') ? 'OPEN' : 'RESOLVED';
    try {
      await updatePunchListItem({
        itemId,
        scope: 'SESSION',
        sessionID,
        status: newStatus,
      });
      mutatePunchList();
    } catch (err) {
      console.error('Failed to toggle punch list item:', err);
    }
  }, [punchListData, sessionID, mutatePunchList]);

  // Punch list drill-down
  const handlePunchListDrillDown = useCallback((itemId) => {
    setPunchListParent(itemId);
  }, []);

  // Punch list breadcrumb navigation
  const handlePunchListNavigate = useCallback((itemId) => {
    setPunchListParent(itemId); // null = back to top level
  }, []);

  const messages = data?.messages || [];
  const chatState = data?.state || null;

  // Filter out tool results
  // Hide tool results (shown as chips) and bootstrap messages — system-routed triggers such as an
  // investigation's automatic continuation — the same rule as SidePanelChat.
  const displayMessages = messages.filter((msg) =>
    msg.message_type !== MessageType.TOOL_RESULT && msg.metadata?.bootstrap !== true);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [displayMessages]);

  // Handle new chat
  const handleNewChat = () => {
    const newsessionID = generatesessionID();
    console.log('Starting new chat:', newsessionID);
    setsessionID(newsessionID);
    setMenuAnchor(null);
  };

  // Handle chat selection
  const handleSelectChat = (selectedsessionID) => {
    console.log('Switching to chat:', selectedsessionID);
    setsessionID(selectedsessionID);
    setMenuAnchor(null);
  };

  // Send message with optimistic update
  const handleSend = async () => {
    if (!input.trim() || isSending || !sessionID) return;

    const messageContent = input.trim();
    setInput('');
    setIsSending(true);

    // Create optimistic message
    const optimisticMessage = {
      message_id: `temp_${Date.now()}`,
      message_type: MessageType.USER,
      user_name: userProfile?.currentUser?.userID || 'User',
      content: messageContent,
      timestamp: new Date().toISOString(),
      sessionID: sessionID,
    };

    try {
      // Optimistic update: add message AND set state to AI_PROCESSING
      // This prevents double-sends by disabling input while AI processes
      mutate(
        (currentData) => {
          // Handle case where currentData might be undefined
          if (!currentData) {
            return {
              sessionID: sessionID,
              mode: 'conversation',
              messages: [optimisticMessage],
              message_count: 1,
              total_messages: 1,
              is_empty_chat: false,
              state: {
                session_id: sessionID,
                state: ChatState.AI_PROCESSING,  // Optimistically set to AI thinking
                last_updated: new Date().toISOString(),
                last_message_id: optimisticMessage.message_id,
              }
            };
          }
          return {
            ...currentData,
            messages: [...(currentData.messages || []), optimisticMessage],
            message_count: (currentData.message_count || 0) + 1,
            state: {
              ...currentData.state,
              state: ChatState.AI_PROCESSING,  // Optimistically set to AI thinking
              last_updated: new Date().toISOString(),
              last_message_id: optimisticMessage.message_id,
            }
          };
        },
        false // Don't revalidate - let polling handle it
      );

      // Send to backend
      const result = await sendChatMessageV2({
        message: messageContent,
        sessionID: sessionID
      });

      console.log('Chat message sent:', result);

    } catch (err) {
      console.error('Error sending message:', err);
      // Revalidate on error to remove optimistic update
      mutate();
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isProcessing = chatState?.state === ChatState.AI_PROCESSING ||
    chatState?.state === ChatState.AI_TOOL_USE;

  // Re-focus the chat input when the agent finishes a turn. The
  // textarea is `disabled` while the agent is processing, which
  // blurs it; without re-focus, the user has to click back into the
  // field every reply. Triggers only on the true → false transition.
  const inputRef = useRef(null);
  const wasProcessingRef = useRef(false);
  useEffect(() => {
    if (wasProcessingRef.current && !isProcessing) {
      inputRef.current?.focus();
    }
    wasProcessingRef.current = isProcessing;
  }, [isProcessing]);

  // Show loading while initializing
  if (!initialized || !sessionID) {
    return (
      <Paper elevation={3} sx={{ height: '100%', maxWidth: 800, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  // Show chat interface
  return (
    <Paper elevation={3} sx={{ height: '100%', maxWidth: 800, mx: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            startIcon={<ChatIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            variant="text"
            sx={{ textTransform: 'none' }}
          >
            <Typography variant="h6" fontWeight={600}>
              Case Analysis Chat
            </Typography>
          </Button>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            PaperProps={{
              sx: { width: 320, maxHeight: 400 }
            }}
          >
            <MenuItem onClick={handleNewChat}>
              <AddIcon sx={{ mr: 1 }} />
              <ListItemText primary="New Chat" />
            </MenuItem>
            <Divider />
            {chatList.length > 0 ? (
              chatList.map((chat) => (
                <MenuItem
                  key={chat.sessionID}
                  onClick={() => handleSelectChat(chat.sessionID)}
                  selected={chat.sessionID === sessionID}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" noWrap>
                        {chat.preview || 'New conversation'}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {new Date(chat.last_updated).toLocaleDateString()}
                      </Typography>
                    }
                  />
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>
                <ListItemText
                  primary={
                    <Typography variant="body2" color="text.secondary" align="center">
                      No previous chats
                    </Typography>
                  }
                />
              </MenuItem>
            )}
          </Menu>
        </Stack>
        <StatusChip state={chatState} />
      </Box>

      {/* Punch List (collapsible, above messages) */}
      {(punchListData?.items?.length > 0 || punchListData?.breadcrumb?.length > 0) && (
        <Box sx={{
          borderBottom: 1, borderColor: 'divider',
          bgcolor: '#f0fdfa', px: 2, py: 1.5, flexShrink: 0,
          maxHeight: 200, overflowY: 'auto',
        }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
              Punch List
            </Typography>
            {punchListData?.progress && (
              <Typography variant="caption" color="text.secondary">
                {punchListData.progress.resolved}/{punchListData.progress.total} resolved
              </Typography>
            )}
          </Stack>
          {/* Breadcrumb */}
          {punchListData?.breadcrumb?.length > 0 && (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
              <Typography
                variant="caption"
                sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                onClick={() => handlePunchListNavigate(null)}
              >
                Top
              </Typography>
              {punchListData.breadcrumb.map((crumb) => (
                <React.Fragment key={crumb.itemId}>
                  <Typography variant="caption" color="text.secondary">&rsaquo;</Typography>
                  <Typography
                    variant="caption"
                    sx={{ cursor: 'pointer', color: 'primary.main', textDecoration: 'underline' }}
                    onClick={() => handlePunchListNavigate(crumb.itemId)}
                  >
                    {crumb.title.length > 30 ? crumb.title.slice(0, 30) + '...' : crumb.title}
                  </Typography>
                </React.Fragment>
              ))}
            </Stack>
          )}
          {/* Items */}
          {(punchListData?.items || []).map((item) => {
            const isDone = item.status === 'RESOLVED' || item.status === 'DISMISSED';
            const priorityColors = { CRITICAL: '#dc2626', HIGH: '#f59e0b', MEDIUM: '#0d9488', LOW: '#9ca3af' };
            return (
              <Stack key={item.item_id} direction="row" spacing={1} alignItems="flex-start" sx={{ py: 0.25 }}>
                <Box
                  component="span"
                  onClick={() => handlePunchListToggle(item.item_id)}
                  sx={{
                    width: 14, height: 14, flexShrink: 0, mt: '3px',
                    border: '2px solid', borderRadius: '3px', cursor: 'pointer',
                    borderColor: isDone ? 'success.main' : (priorityColors[item.priority] || '#0d9488'),
                    bgcolor: isDone ? 'success.main' : 'transparent',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    textDecoration: isDone ? 'line-through' : 'none',
                    color: isDone ? 'text.disabled' : 'text.secondary',
                    cursor: item.child_count > 0 ? 'pointer' : 'default',
                    fontSize: '0.8rem',
                  }}
                  onClick={() => item.child_count > 0 && handlePunchListDrillDown(item.item_id)}
                >
                  {item.title}
                  {item.child_count > 0 && (
                    <Chip label={`${item.resolved_child_count}/${item.child_count}`} size="small" sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }} />
                  )}
                </Typography>
              </Stack>
            );
          })}
        </Box>
      )}

      {/* Messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: 'grey.50',
        }}
      >
        {displayMessages.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <BotIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Start a conversation
            </Typography>
            <Typography variant="body2" color="text.secondary">
              💡 Ask about transactions, patterns, or case analysis
            </Typography>
          </Box>
        ) : (
          <>
            {displayMessages.map((message) => (
              <ChatMessage key={message.message_id} message={message} />
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            inputRef={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about transactions, patterns, or analysis..."
            disabled={isSending || isProcessing}
            variant="outlined"
            size="small"
          />
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={!input.trim() || isSending || isProcessing}
            endIcon={isSending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{ minWidth: 100 }}
          >
            {isSending ? 'Sending' : 'Send'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}